<script lang="ts">
	import { beforeNavigate } from "$app/navigation";
	import IshavskraftLogo from "#lib/assets/IshavskraftLogo.svelte";
	import { Button } from "@ishavskraft/shared";
	import Search from "./Search.svelte";

	const toggleMenu = (e: Event) => {
		// open dialog if supported
		if (typeof HTMLDialogElement === "function") {
			e.preventDefault();

			if (!menuInit) {
				menuInit = true;

				// clone #mer content and prepend to dialog
				const moreContent = document.querySelector("#mer .menu-content");

				if (moreContent) {
					const clone = moreContent.cloneNode(true);
					// eslint-disable-next-line svelte/no-dom-manipulating
					dialogMenu?.appendChild(clone);
					// change nav aria-label for unique landmark
					dialogMenu?.querySelector("nav")?.setAttribute("aria-label", "Hovedsider via Meny");
				}
			}

			dialogMenu?.showModal();
		}
	};

	const toggleMyPage = (e: Event) => {
		// open dialog if supported
		if (typeof HTMLDialogElement === "function") {
			e.preventDefault();
			dialogMyPage?.showModal();
		}
	};

	let menuInit = false;
	interface Props {
		class?: string;
	}

	let { class: classes = "" }: Props = $props();
	let dialogMenu: HTMLDialogElement | undefined = $state();
	let dialogMyPage: HTMLDialogElement | undefined = $state();

	beforeNavigate(({ shallow, type }) => {
		if (shallow && type === "goto") return;

		dialogMenu?.close();
		dialogMyPage?.close();
	});
</script>

<header class="text-base {classes}">
	<a href="#hovedinnhold" class="button button-primary not-focus:sr-only absolute z-8 left-.5edge-x"
		>Hopp til hovedinnholdet</a
	>

	<div class="grid grid-cols-[1fr_auto] tablet:grid-cols-[auto_1fr] tablet:gap-6 items-start">
		<p class="m-0 relative top-1 tablet:top-.5 h-48px w-143px">
			<a
				href="/"
				class="logo fixed z-7 left-edge-x laptop:left-[calc(var(--space-edge-x)-.75rem)] leading-[3] outline-none text-$focus focus:shadow-[0_3px]"
				aria-label="Ishavskraft"
			>
				<IshavskraftLogo />
			</a>
		</p>

		<nav aria-label="Global" class="ishavskraft-global">
			<ul
				class="m-0 p-0 list-none grid grid-cols-[repeat(3,auto)_1fr] gap-$_s items-start color-$primary"
			>
				<li>
					<a
						class="hidden tablet:inline-block button button-link min-w-0 px-0 button-selected"
						href="/">Privat</a
					>
				</li>
				<li>
					<a
						class="hidden tablet:inline-block button button-link min-w-0 px-0 hover:underline focus:underline"
						href="//bedriftskunde.ishavskraft.no">Bedrift</a
					>
				</li>
				<li>
					<a
						class="hidden button button-link min-w-0 px-0 tablet:inline-block hover:underline focus:underline"
						href="//bedriftskunde.ishavskraft.no/konsesjonskraft">Konsesjonskraft</a
					>
				</li>
				<li
					class="flex items-start gap-$_s fixed top-2 right-edge-x laptop:right-[calc(var(--space-edge-x)-.75rem)] z-10 fixed-container"
				>
					<Search />
					<p class="m-0 flex justify-end items-start">
						<a
							class="button bg-white min-w-0 px-3 grid tablet:grid-cols-[minmax(0,1fr)_auto] justify-center items-center gap-[.3ch] landscape:pr-2 max-landscape:w-[3rem] py-0 min-h-[3rem]"
							onclick={toggleMyPage}
							href="//app.ishavskraft.no"
							rel="nofollow"
							aria-label="Min side"
						>
							<span class="hidden tablet:inline">
								<span class="max-landscape:sr-only">Min side</span>
							</span>
							<span class="w-1.5rem relative h-1.5rem">
								<span class="block w-1.5rem h-1.5rem items-center">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="#002987"
										aria-hidden="true"
									>
										<path
											d="M12 12C10.9 12 9.95833 11.6083 9.175 10.825C8.39167 10.0417 8 9.1 8 8C8 6.9 8.39167 5.95833 9.175 5.175C9.95833 4.39167 10.9 4 12 4C13.1 4 14.0417 4.39167 14.825 5.175C15.6083 5.95833 16 6.9 16 8C16 9.1 15.6083 10.0417 14.825 10.825C14.0417 11.6083 13.1 12 12 12ZM4 20V17.2C4 16.6333 4.146 16.1127 4.438 15.638C4.73 15.1633 5.11733 14.8007 5.6 14.55C6.63333 14.0333 7.68333 13.646 8.75 13.388C9.81667 13.13 10.9 13.0007 12 13C13.1 12.9993 14.1833 13.1287 15.25 13.388C16.3167 13.6473 17.3667 14.0347 18.4 14.55C18.8833 14.8 19.271 15.1627 19.563 15.638C19.855 16.1133 20.0007 16.634 20 17.2V20H4ZM6 18H18V17.2C18 17.0167 17.9543 16.85 17.863 16.7C17.7717 16.55 17.6507 16.4333 17.5 16.35C16.6 15.9 15.6917 15.5627 14.775 15.338C13.8583 15.1133 12.9333 15.0007 12 15C11.0667 14.9993 10.1417 15.112 9.225 15.338C8.30833 15.564 7.4 15.9013 6.5 16.35C6.35 16.4333 6.229 16.55 6.137 16.7C6.045 16.85 5.99933 17.0167 6 17.2V18ZM12 10C12.55 10 13.021 9.80433 13.413 9.413C13.805 9.02167 14.0007 8.55067 14 8C13.9993 7.44933 13.8037 6.97867 13.413 6.588C13.0223 6.19733 12.5513 6.00133 12 6C11.4487 5.99867 10.978 6.19467 10.588 6.588C10.198 6.98133 10.002 7.452 10 8C9.998 8.548 10.194 9.019 10.588 9.413C10.982 9.807 11.4527 10.0027 12 10Z"
											fill="#002987"
										></path>
									</svg>
								</span>
							</span>
						</a>
					</p>
					<p class="m-0">
						<a
							onclick={toggleMenu}
							class="min-w-0 button bg-white px-3 grid tablet:grid-cols-[minmax(0,1fr)_auto] justify-center items-center gap-[.5ch] py-0 min-h-[3rem]"
							href="#mer"
							aria-label="Meny"
						>
							<span class="hidden tablet:inline">Meny</span>
							<span class="hamburger-icon"></span>
						</a>
					</p>
				</li>
			</ul>
		</nav>
	</div>
</header>

<dialog
	bind:this={dialogMyPage}
	class="mt-[4.275rem] mr-edge-x py-2 landscape:pb-6"
	onclick={(event) => {
		const rect = dialogMyPage?.getBoundingClientRect();
		const isInDialog =
			rect &&
			rect.top <= event.clientY &&
			event.clientY <= rect.top + rect.height &&
			rect.left <= event.clientX &&
			event.clientX <= rect.left + rect.width;
		if (!isInDialog) dialogMyPage?.close();
	}}
>
	<p class="text-end my-0">
		<button
			onclick={() => dialogMyPage?.close()}
			class="close-button button button-link relative right-[calc(-1*var(--p-button,0))] min-w-[3rem] px-0"
		>
			<span class="sr-only">Lukk</span> <span class="close-icon"></span>
		</button>
	</p>
	<h2 class="sr-only">Velg innlogging</h2>
	<ul class="list-none p-0 grid gap-x-4 gap-y-2 my-2">
		<li>
			<Button
				class="bg-$glod border-$aurora plausible-event-name=Min+side"
				href="//app.ishavskraft.no"
				arrow={true}
				rel="nofollow">Min side</Button
			>
		</li>
		<li>
			<Button
				href="//bedrift.ishavskraft.no/"
				class="align-start plausible-event-name=Online+bedrift"
				rel="nofollow">Online bedrift</Button
			>
		</li>
	</ul>
</dialog>

<dialog
	bind:this={dialogMenu}
	class="menu pb-16 text-body w-full max-w-none px-edge-x m-0 max-h-[100dvh] rounded-0"
	onclick={(event) => {
		const rect = dialogMenu?.getBoundingClientRect();
		const isInDialog =
			rect &&
			rect.top <= event.clientY &&
			event.clientY <= rect.top + rect.height &&
			rect.left <= event.clientX &&
			event.clientX <= rect.left + rect.width;
		if (!isInDialog) dialogMenu?.close();
	}}
>
	<p class="absolute top-[1.25em] right-edge-x my-0">
		<button
			onclick={() => dialogMenu?.close()}
			class="close-button button button-link relative right-[calc(-1*var(--p-button,0))] min-w-[3rem]"
		>
			<span class="sr-only">Lukk</span> <span class="close-icon"></span>
		</button>
	</p>
</dialog>

<style lang="postcss">
	/* variable to use for sticky top elements, takes Header fixed height into account */
	:global(:root) {
		--sticky-top: 5rem;
	}

	header {
		--_s: var(--s-2);
		--logo-svg-height: 1.75em;

		padding: var(--s-2) var(--space-edge-x);

		:where(:global(a, button)) {
			min-width: 3rem;
			height: 3rem;
		}

		:where(:global(a:not(.button))) {
			display: inline-block;
		}
	}

	@media (--tablet) {
		header {
			--_s: var(--s-3);
		}
	}

	@media (--laptop) {
		header {
			--_s: var(--s-4);
		}
	}

	.logo {
		&::after {
			content: "";
			background: color-mix(in oklab, var(--bg-root, var(--bg)) 25%, transparent);
			border-radius: var(--rounded-pill);
			filter: blur(0.5rem);
			position: absolute;
			inset: 0 calc(-1 * min(var(--space-edge-x), var(--p-button)));
			z-index: -1;
		}

		& :global(svg) {
			color: var(--primary);
			filter: drop-shadow(
				0 0 3px color-mix(in oklab, transparent, var(--bg-root, var(--bg)) 87.5%)
			);
			height: var(--logo-svg-height);
			width: auto;
		}
	}

	.ishavskraft-global,
	.fixed-container {
		padding-block-start: calc(0.125 * var(--logo-svg-height) + 1px);
	}

	.hamburger-icon {
		--_line-height: 2px;
		background: no-repeat center / 100% var(--_line-height)
			linear-gradient(currentColor, currentColor);
		display: inline-block;
		height: 14px; /* calculate from --text-base and adjust px size for --_line-height */
		width: 1.25em;
		border-top: var(--_line-height) solid;
		border-bottom: var(--_line-height) solid;
	}

	dialog {
		filter: drop-shadow(0 1.5rem 2rem rgb(0 0 0 / 0.5));
		&::backdrop {
			background: none;
		}
	}

	/* Match the front-page hero icon colour for the menu icons inside the Meny dialog only.
	   The footer keeps its existing colour because that rule is scoped to this component. */
	dialog.menu :global(nav svg) {
		--color: var(--lysning);
	}

	.close-button:focus:not(:hover, :active, :focus-visible) {
		border-color: transparent;
		box-shadow: none;
	}
</style>
